'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { Logo } from '@/components/brand/logo';
import { cn } from '@/lib/utils';
import { SellerQueryProvider } from './providers';
import { SellerTopBar } from '@/components/seller/seller-top-bar';
import { useAuthStore } from '@/stores/auth';

const navItems = [
  { href: '/seller',          label: 'Dashboard' },
  { href: '/seller/inbox',    label: 'Inbox' },
  { href: '/seller/listings', label: 'Listings' },
  { href: '/seller/orders',   label: 'Orders' },
  { href: '/seller/payouts',  label: 'Payouts' },
  { href: '/seller/statements', label: 'Statements' },
  { href: '/seller/reviews', label: 'Reviews' },
  { href: '/seller/team', label: 'Team' },
  { href: '/seller/settings', label: 'Settings' },
];

export default function SellerLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isLoading } = useAuthStore();

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      router.replace('/login');
      return;
    }
    if (!user.store_id && pathname !== '/seller/no-store') {
      router.replace('/seller/no-store');
    }
  }, [isLoading, user, pathname, router]);

  // Bypass the shell entirely on the no-store landing page.
  if (pathname === '/seller/no-store') {
    return <>{children}</>;
  }

  if (isLoading || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-ink/60">
        Loading…
      </div>
    );
  }

  const topBarLabel = user?.name ? `${user.name}'s store` : 'Seller dashboard';

  return (
    <SellerQueryProvider>
      <div className="flex min-h-screen">
        <aside className="w-64 shrink-0 border-r border-forest/10 bg-white p-6">
          <Link href="/seller" className="block">
            <Logo height={24} />
          </Link>
          <p className="mt-0.5 text-xs text-forest/50">Seller Dashboard</p>
          <nav className="mt-8 flex flex-col gap-1">
            {navItems.map((item) => {
              const isActive =
                pathname === item.href ||
                (item.href !== '/seller' && pathname.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={cn(
                    'rounded px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-l-2 border-terracotta bg-terracotta/10 text-terracotta'
                      : 'text-forest/70 hover:bg-forest/5 hover:text-forest',
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>
        <div className="flex flex-1 flex-col">
          <SellerTopBar storeName={topBarLabel} />
          <main className="flex-1 bg-bone p-8">{children}</main>
        </div>
      </div>
    </SellerQueryProvider>
  );
}
